<template>
  <div
    class="w-full flex flex-row h-fit sm:h-420 overflow-visible slide-show-desktop"
  >
    <div
      v-if="props.images"
      class="w-full flex flex-col relative items-center justify-end"
    >
      <div
        id="img-slider-slides"
        ref="scrollRef"
        class="w-full h-full flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory md:relative md:justify-center hide-scrollbar"
      >
        <div
          v-for="(image, index) in props.images"
          :id="'img-container-' + index"
          :key="'image' + image.ProductAttributeCombinationId"
          :ref="(el) => setRef(index, el)"
          class="w-full h-full flex justify-center shrink-0 snap-start scroll-smooth md:absolute md:shrink"
          :class="index == currentImg ? 'md:opacity-1' : 'md:opacity-0'"
          :aria-hidden="index !== currentImg ? true : undefined"
        >
          <NuxtImg
            :src="image.PictureUrl"
            :alt="image.AlternativeText ?? ''"
            sizes="280px lg:400px"
            format="webp"
            quality="90"
            provider="cloudfront"
          />
        </div>

        <div v-if="props.images.length === 0">
          <NuxtImg
            src="/images/product_related/product_placeholder.svg"
            alt="Produktbild saknas för produkten"
            class="h-full"
            provider="cloudfront"
          />
        </div>
      </div>

      <div
        v-if="props.images.length > 0"
        class="flex gap-40 pt-16 justify-center items-center"
      >
        <div aria-live="polite" aria-atomic="true" class="sr-only">
          {{ liveRegionText }}
        </div>
        <AppButton
          lang="sv"
          color="secondary"
          size="sm"
          class="min-w-44"
          :full-width="true"
          :aria-label="prevLabel"
          aria-controls="img-slider-slides"
          :disabled="currentImg == 0 ? true : false"
          @click="currentSlide(currentImg, 'prev')"
        >
          <nuxt-icon name="arrow-left" aria-hidden="true" />
        </AppButton>
        <div
          class="flex gap-16 pt-16 justify-center items-center"
          aria-hidden="true"
        >
          <span
            v-for="(image, index) in props.images"
            :key="'image' + image.ProductAttributeCombinationId"
            class="block h-8 rounded-full transition-all"
            :class="
              index == currentImg
                ? 'w-24 bg-icon-warning'
                : 'w-8 bg-border-tertiary'
            "
          ></span>
        </div>
        <AppButton
          lang="sv"
          color="secondary"
          size="sm"
          class="min-w-44"
          :full-width="true"
          :aria-label="nextLabel"
          aria-controls="img-slider-slides"
          :disabled="currentImg == props.images.length - 1 ? true : false"
          @click="currentSlide(currentImg, 'next')"
        >
          <nuxt-icon name="arrow-right" aria-hidden="true" />
        </AppButton>
      </div>
    </div>
    <div v-else class="w-full flex flex-col relative items-center justify-end">
      <NuxtImg
        src="/images/product_related/product_placeholder.svg"
        alt="Produktbild saknas för produkten"
        class="h-full"
        provider="cloudfront"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { ComponentPublicInstance } from 'vue';
import type { ImageDto } from '~/models/ListingProduct.api.model';
const elementRefs = ref<Record<number, Element>>({});

const setRef = (
  index: number,
  el: Element | ComponentPublicInstance | null,
) => {
  if (el && el instanceof Element) {
    elementRefs.value[index] = el;
  }
};

const props = withDefaults(
  defineProps<{
    images?: ImageDto[];
  }>(),
  {
    images: () => [],
  },
);
const currentImg: Ref<number> = ref(0);
const scrollRef: Ref<HTMLDivElement | null> = ref(null);
const liveRegionText: Ref<string> = ref('');

const prevLabel = computed(
  () =>
    `Visa föregående bild (${currentImg.value + 1} av ${props.images.length})`,
);
const nextLabel = computed(
  () => `Visa nästa bild (${currentImg.value + 1} av ${props.images.length})`,
);

watch(currentImg, (newIndex) => {
  liveRegionText.value = `Bild ${newIndex + 1} av ${props.images.length}`;
});

onMounted(() => {
  useEventListener(scrollRef, 'scroll', (e) => {
    if (e.target) {
      const scrollAmount = (e.target as any).scrollLeft as number;
      const targetwidth = (e.target as any).clientWidth as number;
      const scrollValue = scrollAmount / targetwidth;

      if (scrollValue % 1 === 0) {
        currentImg.value = scrollValue;
      }
    }
  });
});

const calcAmountToScrollInPixels = (index: number) => {
  return elementRefs.value[index].clientWidth as number;
};

const currentSlide = (index: number, type: string) => {
  if (type == 'next' && currentImg.value < props.images.length - 1) {
    currentImg.value += 1;
    if (scrollRef.value) {
      scrollRef.value.scrollLeft += calcAmountToScrollInPixels(index + 1);
    }
  } else if (type == 'prev' && currentImg.value > 0) {
    currentImg.value -= 1;
    if (scrollRef.value) {
      scrollRef.value.scrollLeft -= calcAmountToScrollInPixels(index - 1);
    }
  }
};
</script>

<style scoped>
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
